import { ReactNode, useMemo, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { QuickTableChangeDialog } from "@/components/QuickTableChangeDialog";
import { ManualReplyDialog } from "@/components/ManualReplyDialog";
import { ChangeTableConfirmDialog } from "@/components/ChangeTableConfirmDialog";
import { SwapReservationPickerDialog } from "@/components/SwapReservationPickerDialog";
import { SwapConfirmDialog } from "@/components/SwapConfirmDialog";
import {
  computeChangeWarnings,
  computeSwapWarnings,
  toTargetTable,
  type MoveWarning,
} from "@/lib/reservationMoveWarnings";
import { formatCustomerName } from "@/lib/clientUtils";
import {
  Check,
  X,
  Clock,
  Mail,
  Edit,
  Phone,
  Users,
  Baby,
  ArrowRightLeft,
  StickyNote,
  UserX,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import {
  Table,
  CombinedTable,
  TableAvailability,
  TableWeekdayAvailability,
  Reservation,
} from "@/types/table";
import {
  DashboardReservation,
  ReservationStatusInfo,
  SendReservationEmailPayload,
} from "@/types/reservation";

const SOURCE_LABELS: Record<string, string> = {
  walk_in: "Walk-in",
  widget_public: "Site web",
  public_widget: "Site web",
  widget: "Site web",
  phone: "Téléphone",
  instagram: "Instagram",
  manual: "Saisie manuelle",
  dashboard: "Saisie manuelle",
  other: "Autre",
};

const formatSource = (source?: string) => {
  if (!source) return null;
  return SOURCE_LABELS[source] ?? source;
};


export interface ReservationPreviewContentProps {
  reservation: DashboardReservation;
  endTime: string;
  tables: Table[];
  combinedTables: CombinedTable[];
  convertedReservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  getReservationStatus: (reservation: DashboardReservation) => ReservationStatusInfo;
  getLoyaltyBadge: (email: string | undefined) => ReactNode;
  getNoShowBadge: (email: string | undefined) => ReactNode;
  getCustomerNotes: (email: string | undefined) => string | null;
  onUpdateStatus: (
    reservationId: number,
    status: "arrivé" | "annulé" | "planifié",
    cancellationReason?: string,
  ) => void;
  onEdit: (reservation: DashboardReservation) => void;
  onQuickTableChange: (
    reservationId: number,
    tableId: string | null,
    combinedTableId: string | null,
  ) => Promise<void> | void;
  onSendEmail: (data: SendReservationEmailPayload) => void;
  sendingEmail: number | null;
  onSwapSelect?: (reservation: DashboardReservation) => void;
  /** When provided, "Échanger" opens a picker → confirm flow that calls this RPC bridge. */
  onSwapTables?: (aSupabaseId: string, bSupabaseId: string) => Promise<void> | void;
  reservationSettings?: {
    bookingMode: "slot" | "time";
    reservationDurationMinutes: number;
  };
}

/**
 * Stateless body used by both:
 * - TimelineReservationPopover (compact popover on the timeline blocks)
 * - ReservationPreviewDialog (unified preview dialog opened on click)
 *
 * Keeps header + details + quick actions visually identical across surfaces.
 */
export const ReservationPreviewContent = ({
  reservation,
  endTime,
  tables,
  combinedTables,
  convertedReservations,
  availabilities,
  weekdayAvailabilities,
  getReservationStatus,
  getLoyaltyBadge,
  getNoShowBadge,
  getCustomerNotes,
  onUpdateStatus,
  onEdit,
  onQuickTableChange,
  onSendEmail,
  sendingEmail,
  onSwapSelect,
  onSwapTables,
  reservationSettings,
}: ReservationPreviewContentProps) => {
  const status = getReservationStatus(reservation);
  const customerNotes = getCustomerNotes(reservation.email);

  const tableName = (() => {
    if (reservation.tableId) {
      const table = tables.find((t) => t.id === reservation.tableId);
      return table?.name || "Table supprimée";
    }
    if (reservation.combinedTableId) {
      const ct = combinedTables.find((c) => c.id === reservation.combinedTableId);
      return ct?.name || "Table combinée supprimée";
    }
    return "Non assignée";
  })();

  // === Change-table confirmation flow ===
  const [pendingChange, setPendingChange] = useState<{
    tableId: string | null;
    combinedTableId: string | null;
    targetName: string;
    targetCapacity: number;
    targetIsCombined: boolean;
    targetIsTerrace?: boolean;
  } | null>(null);
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [manualReplyOpen, setManualReplyOpen] = useState(false);

  const combinedTablesById = useMemo(() => {
    const m = new Map<string, string[]>();
    combinedTables.forEach((c) => m.set(c.id, c.tableIds));
    return m;
  }, [combinedTables]);

  const pendingWarnings = useMemo<MoveWarning[]>(() => {
    if (!pendingChange || !reservationSettings) return [];
    const target = pendingChange.combinedTableId
      ? combinedTables.find((c) => c.id === pendingChange.combinedTableId)
      : tables.find((t) => t.id === pendingChange.tableId);
    if (!target) return [];
    const targetTable = toTargetTable(
      target as any,
      pendingChange.combinedTableId ? "combined" : "simple",
      combinedTablesById,
    );
    return computeChangeWarnings({
      reservation: {
        id: String(reservation.id),
        supabaseId: reservation.supabaseId,
        tableId: reservation.tableId,
        combinedTableId: reservation.combinedTableId,
        numberOfPeople: reservation.persons,
        terracePreference: (reservation.terracePreference as any) ?? undefined,
        durationMinutes: undefined,
        startTime: reservation.time,
        date: reservation.reservationDate,
        status: "planifié",
      },
      target: targetTable,
      allReservations: convertedReservations.map((r) => ({
        supabaseId: r.supabaseId,
        id: r.id,
        startTime: r.startTime,
        status: r.status,
        durationMinutes: r.durationMinutes,
        date: r.date,
        tableId: r.tableId,
        combinedTableId: r.combinedTableId,
      })),
      combinedTablesById,
      bookingMode: reservationSettings.bookingMode,
      defaultDurationMinutes: reservationSettings.reservationDurationMinutes,
      blockedDates: availabilities,
      weekdayAvailabilities,
      // NOTE: QuickTableChangeDialog filtre déjà les tables hors période bookable,
      // donc on n'a pas besoin de re-vérifier ici (la liste ne propose que des cibles valides).
      bookablePeriods: [],
    });
  }, [
    pendingChange,
    reservation,
    tables,
    combinedTables,
    combinedTablesById,
    convertedReservations,
    availabilities,
    weekdayAvailabilities,
    reservationSettings,
  ]);

  // === Swap flow (picker → confirm → RPC) ===
  const [swapPickerOpen, setSwapPickerOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState<typeof convertedReservations[number] | null>(null);
  const [swapSubmitting, setSwapSubmitting] = useState(false);

  const swapWarnings = useMemo(() => {
    if (!swapTarget || !reservationSettings) {
      return { aWarnings: [] as MoveWarning[], bWarnings: [] as MoveWarning[] };
    }
    // A keeps its current reservation but gets B's table; B gets A's.
    const aCurrentTableId = reservation.tableId;
    const aCurrentCombinedId = reservation.combinedTableId;
    const bCurrentTableId = swapTarget.tableId;
    const bCurrentCombinedId = swapTarget.combinedTableId;

    const aTargetSource = bCurrentCombinedId
      ? combinedTables.find((c) => c.id === bCurrentCombinedId)
      : tables.find((t) => t.id === bCurrentTableId);
    const bTargetSource = aCurrentCombinedId
      ? combinedTables.find((c) => c.id === aCurrentCombinedId)
      : tables.find((t) => t.id === aCurrentTableId);
    if (!aTargetSource || !bTargetSource) {
      return { aWarnings: [], bWarnings: [] };
    }

    return computeSwapWarnings({
      a: {
        id: String(reservation.id),
        supabaseId: reservation.supabaseId,
        tableId: aCurrentTableId,
        combinedTableId: aCurrentCombinedId,
        numberOfPeople: reservation.persons,
        terracePreference: (reservation.terracePreference as any) ?? undefined,
        startTime: reservation.time,
        date: reservation.reservationDate,
        status: "planifié",
      },
      b: {
        id: String(swapTarget.id),
        supabaseId: swapTarget.supabaseId,
        tableId: bCurrentTableId,
        combinedTableId: bCurrentCombinedId,
        numberOfPeople: swapTarget.numberOfPeople,
        terracePreference: swapTarget.terracePreference,
        startTime: swapTarget.startTime,
        date: swapTarget.date,
        status: "planifié",
      },
      aTarget: toTargetTable(
        aTargetSource as any,
        bCurrentCombinedId ? "combined" : "simple",
        combinedTablesById,
      ),
      bTarget: toTargetTable(
        bTargetSource as any,
        aCurrentCombinedId ? "combined" : "simple",
        combinedTablesById,
      ),
      allReservations: convertedReservations.map((r) => ({
        supabaseId: r.supabaseId,
        id: r.id,
        startTime: r.startTime,
        status: r.status,
        durationMinutes: r.durationMinutes,
        date: r.date,
        tableId: r.tableId,
        combinedTableId: r.combinedTableId,
      })),
      combinedTablesById,
      bookingMode: reservationSettings.bookingMode,
      defaultDurationMinutes: reservationSettings.reservationDurationMinutes,
      blockedDates: availabilities,
      weekdayAvailabilities,
      bookablePeriods: [],
    });
  }, [
    swapTarget,
    reservation,
    tables,
    combinedTables,
    combinedTablesById,
    convertedReservations,
    availabilities,
    weekdayAvailabilities,
    reservationSettings,
  ]);

  const getTableNameById = (tableId?: string, combinedTableId?: string): string => {
    if (tableId) return tables.find((t) => t.id === tableId)?.name ?? "—";
    if (combinedTableId) return combinedTables.find((c) => c.id === combinedTableId)?.name ?? "—";
    return "—";
  };

  const renderStatusBadge = () => {
    switch (status.type) {
      case "arrived":
        return <Badge className="bg-green-500 text-white text-[10px]">Arrivé</Badge>;
      case "no-show":
        return <Badge className="bg-gray-700 text-white text-[10px]">No-show</Badge>;
      case "canceled":
        return <Badge variant="outline" className="text-[10px] text-muted-foreground border-muted-foreground/30 bg-muted/50">Annulé</Badge>;
      case "on-time":
        return <Badge className="bg-green-500 text-white text-[10px]">À l'heure</Badge>;
      case "late":
        return <Badge className="bg-orange-500 text-white text-[10px]">+{status.delay} min</Badge>;
      case "very-late":
        return <Badge variant="destructive" className="text-[10px]">+{status.delay} min</Badge>;
      case "no-show-probable":
        return (
          <Badge variant="destructive" className="bg-gray-700 text-[10px]">
            No-show probable
          </Badge>
        );
      default:
        return <Badge variant="secondary" className="text-[10px]">Planifié</Badge>;
    }
  };

  const isTerrace =
    reservation.terracePreference === "terrace" ||
    (reservation.tableId && tables.find((t) => t.id === reservation.tableId)?.isTerrace) ||
    (reservation.combinedTableId &&
      combinedTables.find((c) => c.id === reservation.combinedTableId)?.isTerrace);

  const defaultDuration = reservationSettings?.reservationDurationMinutes;
  const customDuration =
    reservation.durationMinutes != null &&
    defaultDuration != null &&
    reservation.durationMinutes !== defaultDuration
      ? reservation.durationMinutes
      : null;

  const sourceLabel = formatSource(reservation.source);

  return (
    <>
      {/* Header */}
      <div className="px-3 py-2 border-b flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-semibold text-sm truncate">{reservation.name}</span>
          {reservation.source === "walk_in" && (
            <Badge variant="warning" className="text-[10px] px-1.5 py-0">
              Walk-in
            </Badge>
          )}
          {getLoyaltyBadge(reservation.email)}
          {getNoShowBadge(reservation.email)}
        </div>
        {renderStatusBadge()}
      </div>

      {/* Date + time strip */}
      <div className="px-3 py-2 border-b flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-foreground">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          <span className="capitalize">
            {format(reservation.reservationDate, "EEE d MMM yyyy", { locale: fr })}
          </span>
          <span className="text-muted-foreground">·</span>
          <span>
            {reservation.time} – {endTime}
          </span>
          {customDuration && (
            <span className="text-primary font-medium">({customDuration}′)</span>
          )}
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <Users className="w-3 h-3" />
          {reservation.persons} pers.
          {reservation.numberOfChildren > 0 && (
            <span className="flex items-center gap-0.5">
              <Baby className="w-3 h-3" />+{reservation.numberOfChildren}
            </span>
          )}
          {reservation.hasStroller && (
            <StrollerIcon size={12} className="text-muted-foreground" />
          )}
        </span>
      </div>

      {/* Details */}
      <div className="px-3 py-2 border-b space-y-1.5 text-xs">
        <div className="flex items-center gap-1 text-muted-foreground">
          <ArrowRightLeft className="w-3 h-3" />
          <span className="text-foreground">{tableName}</span>
          {isTerrace && (
            <Badge variant="outline" className="text-[10px] ml-1">
              ☀️ Terrasse
            </Badge>
          )}
        </div>
        {sourceLabel && reservation.source !== "walk_in" && (
          <div className="text-muted-foreground">
            Source : <span className="text-foreground">{sourceLabel}</span>
          </div>
        )}
        <div className="flex items-center gap-1 text-muted-foreground">
          <Phone className="w-3 h-3" />
          {reservation.phone ? (
            <a
              href={`tel:${reservation.phone}`}
              className="text-foreground hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {reservation.phone}
            </a>
          ) : (
            <span>—</span>
          )}
        </div>
        {reservation.email && (
          <div className="flex items-center gap-1 text-muted-foreground">
            <Mail className="w-3 h-3" />
            <span className="text-foreground truncate">{reservation.email}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setManualReplyOpen(true);
              }}
              title="Répondre par email"
              className="ml-1 p-0.5 rounded hover:bg-muted text-primary"
            >
              <Mail className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {status.type === "canceled" && reservation.cancellationReason && (
          <div className="flex items-start gap-1 rounded-md bg-muted/60 border border-border px-2 py-1 text-muted-foreground">
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>
              <span className="font-semibold">Raison : </span>
              {reservation.cancellationReason === "no-show"
                ? "No-show"
                : reservation.cancellationReason}
            </span>
          </div>
        )}
        {reservation.notes && (
          <p className="italic text-amber-600 dark:text-amber-400">{reservation.notes}</p>
        )}
        {reservation.customAnswers && reservation.customAnswers.answers.length > 0 && (
          <p className="text-foreground">
            <span className="font-semibold">{reservation.customAnswers.question} </span>
            {reservation.customAnswers.answers.join(', ')}
          </p>
        )}
        {reservation.internalNotes && (
          <p className="italic text-violet-600 dark:text-violet-400 flex items-center gap-1">
            <StickyNote className="w-3 h-3 flex-shrink-0" />
            {reservation.internalNotes}
          </p>
        )}
        {customerNotes && (
          <p className="italic text-violet-500 flex items-center gap-1">
            <StickyNote className="w-3 h-3 flex-shrink-0" />
            {customerNotes}
          </p>
        )}
      </div>


      {/* Quick status actions */}
      <div className="px-3 py-2 border-b flex items-center gap-1.5">
        {status.type !== "arrived" && status.type !== "no-show" && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1 flex-1"
            onClick={() => onUpdateStatus(reservation.id, "arrivé")}
          >
            <Check className="w-3 h-3" /> Arrivé
          </Button>
        )}
        {status.type !== "canceled" && status.type !== "no-show" && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1 flex-1"
            onClick={() => onUpdateStatus(reservation.id, "annulé")}
          >
            <X className="w-3 h-3" /> Annuler
          </Button>
        )}
        {(status.type === "late" ||
          status.type === "very-late" ||
          status.type === "no-show-probable") && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1 flex-1 text-gray-700 border-gray-400 dark:text-gray-300"
            onClick={() => onUpdateStatus(reservation.id, "annulé", "no-show")}
          >
            <UserX className="w-3 h-3" /> No-show
          </Button>
        )}
        {(status.type === "arrived" ||
          status.type === "canceled" ||
          status.type === "no-show") && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1 flex-1"
            onClick={() => onUpdateStatus(reservation.id, "planifié")}
          >
            <Clock className="w-3 h-3" /> Replanifier
          </Button>
        )}
      </div>

      {/* Secondary actions */}
      <div className="px-3 py-2 flex items-center gap-1.5 flex-wrap">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs gap-1"
          onClick={() => onEdit(reservation)}
        >
          <Edit className="w-3 h-3" /> Modifier
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs gap-1"
          onClick={() => (window.location.href = `tel:${reservation.phone}`)}
        >
          <Phone className="w-3 h-3" /> Appeler
        </Button>
        {reservation.email && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs gap-1"
                disabled={sendingEmail === reservation.id}
              >
                <Mail className="w-3 h-3" /> Email
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-background z-[60]">
              {reservation.status === "annulé" && (
                <DropdownMenuItem
                  onClick={() =>
                    onSendEmail({
                      reservationId: reservation.id,
                      reservationUuid: reservation.supabaseId,
                      emailType: "cancellation",
                      reservationName: reservation.name,
                      reservationEmail: reservation.email!,
                    })
                  }
                >
                  Email d'annulation
                </DropdownMenuItem>
              )}
              {reservation.status === "planifié" && (
                <>
                  <DropdownMenuItem
                    onClick={() =>
                      onSendEmail({
                        reservationId: reservation.id,
                        reservationUuid: reservation.supabaseId,
                        emailType: "confirmation",
                        reservationName: reservation.name,
                        reservationEmail: reservation.email!,
                      })
                    }
                  >
                    Email de confirmation
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      onSendEmail({
                        reservationId: reservation.id,
                        reservationUuid: reservation.supabaseId,
                        emailType: "modification",
                        reservationName: reservation.name,
                        reservationEmail: reservation.email!,
                      })
                    }
                  >
                    Email de modification
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {(onSwapTables || onSwapSelect) && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs gap-1"
            disabled={
              reservation.status === "annulé" ||
              (!reservation.tableId && !reservation.combinedTableId)
            }
            onClick={() => {
              if (onSwapTables) {
                setSwapPickerOpen(true);
              } else if (onSwapSelect) {
                onSwapSelect(reservation);
              }
            }}
          >
            <ArrowRightLeft className="w-3 h-3" /> Échanger
          </Button>
        )}
        <QuickTableChangeDialog
          reservation={{
            id: reservation.id,
            supabaseId: reservation.supabaseId,
            tableId: reservation.tableId,
            combinedTableId: reservation.combinedTableId,
            persons: reservation.persons,
            time: reservation.time,
            reservationDate: reservation.reservationDate,
            customerName: reservation.name,
            isLocked: reservation.isLocked,
          }}
          tables={tables}
          combinedTables={combinedTables}
          allReservations={convertedReservations}
          availabilities={availabilities}
          weekdayAvailabilities={weekdayAvailabilities}
          onTableChange={(_resId, tableId, combinedTableId) => {
            const target = combinedTableId
              ? combinedTables.find((c) => c.id === combinedTableId)
              : tables.find((t) => t.id === tableId);
            if (!target) return;
            const isCombined = !!combinedTableId;
            setPendingChange({
              tableId,
              combinedTableId,
              targetName: target.name,
              targetCapacity: isCombined ? (target as any).totalCapacity : (target as any).capacity,
              targetIsCombined: isCombined,
              targetIsTerrace: (target as any).isTerrace,
            });
          }}
          onSwapTables={onSwapTables ? async (a, b) => { await onSwapTables(a, b); } : undefined}
          disabled={reservation.status === "annulé"}
          reservationSettings={reservationSettings}
        >

          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs gap-1"
            disabled={reservation.status === "annulé"}
            title="Déplacer vers une table libre ou échanger avec une table occupée"
          >
            <ArrowRightLeft className="w-3 h-3" /> Changer
          </Button>
        </QuickTableChangeDialog>
      </div>

      {/* Footer: creation timestamp */}
      <div className="px-3 py-1.5 border-t text-[10px] text-muted-foreground text-right">
        Créée le {format(reservation.createdAt, "d MMM 'à' HH:mm", { locale: fr })}
      </div>

      <ChangeTableConfirmDialog
        open={!!pendingChange}
        onOpenChange={(open) => { if (!open && !changeSubmitting) setPendingChange(null); }}
        reservation={{
          customerName: reservation.name,
          persons: reservation.persons,
          time: reservation.time,
          currentTableName: tableName,
        }}
        pending={pendingChange}
        warnings={pendingWarnings}
        isSubmitting={changeSubmitting}
        onConfirm={async () => {
          if (!pendingChange) return;
          try {
            setChangeSubmitting(true);
            await onQuickTableChange(reservation.id, pendingChange.tableId, pendingChange.combinedTableId);
            setPendingChange(null);
          } finally {
            setChangeSubmitting(false);
          }
        }}
      />

      {onSwapTables && (
        <>
          <SwapReservationPickerDialog
            open={swapPickerOpen}
            onOpenChange={(open) => { if (!open) setSwapPickerOpen(false); }}
            source={{
              id: reservation.id,
              supabaseId: reservation.supabaseId,
              name: reservation.name,
              persons: reservation.persons,
              time: reservation.time,
              reservationDate: reservation.reservationDate,
              tableId: reservation.tableId,
              combinedTableId: reservation.combinedTableId,
            }}
            allReservations={convertedReservations}
            tables={tables}
            combinedTables={combinedTables}
            onPick={(other) => {
              setSwapPickerOpen(false);
              setSwapTarget(other);
            }}
          />
          <SwapConfirmDialog
            open={!!swapTarget}
            onOpenChange={(open) => { if (!open && !swapSubmitting) setSwapTarget(null); }}
            a={{
              customerName: reservation.name,
              persons: reservation.persons,
              time: reservation.time,
              currentTableName: tableName,
              newTableName: swapTarget
                ? getTableNameById(swapTarget.tableId, swapTarget.combinedTableId)
                : "—",
            }}
            b={{
              customerName: swapTarget
                ? (formatCustomerName(swapTarget.customerFirstName, swapTarget.customerLastName) || "Client")
                : "—",
              persons: swapTarget?.numberOfPeople ?? 0,
              time: swapTarget ? swapTarget.startTime.substring(0, 5) : "",
              currentTableName: swapTarget
                ? getTableNameById(swapTarget.tableId, swapTarget.combinedTableId)
                : "—",
              newTableName: tableName,
            }}
            aWarnings={swapWarnings.aWarnings}
            bWarnings={swapWarnings.bWarnings}
            isSubmitting={swapSubmitting}
            onConfirm={async () => {
              if (!swapTarget) return;
              const aId = reservation.supabaseId;
              const bId = swapTarget.supabaseId;
              if (!aId || !bId) return;
              try {
                setSwapSubmitting(true);
                await onSwapTables(aId, bId);
                setSwapTarget(null);
              } finally {
                setSwapSubmitting(false);
              }
            }}
          />
        </>
      )}
      <ManualReplyDialog
        open={manualReplyOpen}
        onOpenChange={setManualReplyOpen}
        target={
          reservation.email && reservation.supabaseId
            ? {
                reservationId: reservation.supabaseId,
                customerName: reservation.name,
                customerEmail: reservation.email,
                date: format(reservation.reservationDate, "yyyy-MM-dd"),
                time: reservation.time,
                numberOfPeople: reservation.persons,
                notes: reservation.notes,
              }
            : null
        }
      />
    </>
  );
};

